import Cookies from 'js-cookie';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import logo from '../assets/img/logo.png';
import AvatarIcon from '../assets/svg/AvatarIcon';
import LogoutIcon from '../assets/svg/LogoutIcon';
import SettingsIcon from '../assets/svg/SettingsIcon';
import { RecordingVisibilityToggle } from '../components/RecordingVisibilityToggle';
import {
  ROUTE_MAP,
  SIDEBAR_ROUTES_BY_USER_ROLE_MAP,
  USER_ROLE,
} from '../constants';
import { useAuthContext } from '../hooks';

export const Layout = ({ children }) => {
  const navigate = useNavigate();
  const { userRole, setUserRole } = useAuthContext();
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);

  const logout = async () => {
    try {
      const response = await fetch('/logout');
      if (response.status !== 200) throw new Error();
      setUserRole(null);
      navigate(ROUTE_MAP.LOGIN, { replace: true });
    } catch {
      toast.error("Can't logout. Please try again!", {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="h-full text-primary">
      <div className="app flex h-full flex-col lg:flex-row">
        <header className="w-full bg-primary bg-cover bg-center bg-no-repeat py-4 lg:h-[100vh] lg:w-72 lg:py-8">
          <div className="flex h-full justify-between gap-10 px-5 lg:flex-col">
            <Link to={ROUTE_MAP.DASHBOARD}>
              <img src={logo} alt="ProspectIQ Logo" className="h-9 w-[248px]" />
            </Link>
            <ul className="hidden flex-1 space-y-1.5 lg:block">
              <SidebarNavigation
                routes={SIDEBAR_ROUTES_BY_USER_ROLE_MAP[userRole]}
                navigate={navigate}
              />
            </ul>
            <div className="hidden lg:block">
              <BottomMenuList
                logout={logout}
                navigate={navigate}
                userRole={userRole}
              />
            </div>
            <button
              type="button"
              onClick={() => {
                setIsSidebarVisible(prev => !prev);
              }}
              className="text-white/70 transition hover:text-white lg:hidden cursor-pointer"
              data-hs-overlay="#mobile-sidebar"
              aria-controls="mobile-sidebar"
              aria-label="Toggle navigation"
            >
              <span className="sr-only">Toggle Navigation</span>
              <svg
                className="h-9 w-9 flex-shrink-0"
                fill="currentColor"
                viewBox="0 0 16 16"
              >
                <path
                  fillRule="evenodd"
                  d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5zm0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5z"
                />
              </svg>
            </button>
          </div>
          <div
            id="mobile-sidebar"
            className={`hs-overlay fixed bottom-0 start-0 top-0 z-[60] w-72 transform overflow-y-auto bg-primary transition-all duration-300 ${isSidebarVisible ? 'translate-x-0' : '-translate-x-full'} [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar]:w-2`}
          >
            <div className="flex h-full flex-col justify-between gap-5 p-5">
              <Link to={ROUTE_MAP.DASHBOARD}>
                <img src={logo} alt="ProspectIQ Logo" className="h-9" />
              </Link>
              <nav
                className="hs-accordion-group mt-3 flex w-full flex-1 flex-col flex-wrap"
                data-hs-accordion-always-open
              >
                <ul className="flex-1 space-y-1.5">
                  <SidebarNavigation
                    routes={SIDEBAR_ROUTES_BY_USER_ROLE_MAP[userRole]}
                    navigate={navigate}
                  />
                </ul>
              </nav>
              <div className="block lg:hidden">
                <BottomMenuList
                  logout={logout}
                  navigate={navigate}
                  userRole={userRole}
                />
              </div>
            </div>
          </div>
        </header>
        <main className="h-full flex-1 overflow-y-auto overflow-x-hidden bg-primary bg-hero bg-cover bg-center bg-no-repeat">
          <div className="h-[100vh] flex flex-col overflow-y-auto overflow-x-hidden rounded-t-2xl bg-neutral-100 lg:rounded-s-[3rem] lg:rounded-tr-none p-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

const SidebarNavigation = ({ routes, navigate }) => {
  return routes.map(({ route, title, icon }) => (
    <li key={route} className="cursor-pointer">
      <span
        className="group flex items-center gap-2 rounded-lg px-2.5 py-2 font-medium text-white transition hover:bg-secondary hover:text-white"
        onClick={() => {
          navigate(route);
        }}
      >
        <span className="text-secondary transition group-hover:text-white">
          {icon}
        </span>
        <span>{title}</span>
      </span>
    </li>
  ));
};

const BottomMenuList = ({ logout, navigate, userRole }) => {
  const fullName = Cookies.get('fullName');
  const isCaller = userRole === USER_ROLE.CALLER;

  return (
    <div className="flex flex-col gap-1">
      <RecordingVisibilityToggle />

      {!isCaller && (
        <button
          className="group flex items-center gap-2 rounded-lg px-2.5 py-2 transition hover:bg-secondary hover:text-white"
          onClick={() => {
            navigate(ROUTE_MAP.SETTINGS);
          }}
        >
          <SettingsIcon className="w-5 h-5 text-brand-300 group-hover:text-white" />
          <span className="text-white font-medium text-[16px]/[24px]">
            Profile Settings
          </span>
        </button>
      )}

      <button
        className="group flex items-center gap-2 rounded-lg px-2.5 py-2 transition hover:bg-secondary hover:text-white"
        onClick={logout}
      >
        <LogoutIcon className="w-5 h-5 text-brand-300 group-hover:text-white" />
        <span className="text-white font-medium text-[16px]/[24px]">
          Log Out
        </span>
      </button>

      <div className="px-2.5 py-2 flex items-center flex-nowrap gap-2 truncate">
        <AvatarIcon className="w-5 h-5 shrink-0" />
        <span className="text-white font-medium text-[16px]/[24px] truncate">
          {fullName}
        </span>
      </div>
    </div>
  );
};
